'use client';

import { FC, useMemo } from 'react';

import { getRoute } from '@ravago/shared/next-data-access';
import { ElasticBrand } from '@ravago/shared/page-data/models/elastic';
import { ProductPortfolioNode } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { DataMapperUtils } from '@ravago/shared/page-data/utils/data-mapper';
import { BrandCard } from '@ravago/shared/radiance/components/card/components/BrandCard/BrandCard';
import { CardGrid } from '@ravago/shared/radiance/components/card/components/CardGrid/CardGrid';
import { useTranslations } from '@ravago/shared/translations/client';

import { useConfigContext } from '../../../../../../hooks/use-config-context';
import { AppUtils } from '../../../../../../utils/app/app.utils';

interface Props {
   brands: Array<ElasticBrand>;
   baseRoute?: string;
   globals: RendererGlobals;
   node: ProductPortfolioNode;
}

export const BrandGrid: FC<Props> = ({ brands, baseRoute, node, globals }) => {
   const translate = useTranslations();
   const { useLegacyDigitalPlatformRoutes } = useConfigContext();

   const { locale, acceptWebp } = globals;

   const brandCards = useMemo(() => brands?.map((brand) => DataMapperUtils.normalizeElasticBrandCard(brand)), [brands, acceptWebp]);

   return (
      <CardGrid>
         {brandCards?.map((brand) => (
            <BrandCard
               key={brand.id}
               href={getRoute(locale, baseRoute, brand.slug, undefined, useLegacyDigitalPlatformRoutes)}
               target="_self"
               noFollow={brand.noFollow}
               name={brand.name}
               producer={brand.producer.name ?? ''}
               logo={brand.producer.logo ?? ''}
               excerpt={brand.excerpt ?? ''}
               polymerTypes={brand.polymerTypes}
               productCount={translate('products-total', { total: brand.numberOfProducts })}
               fillHeight
               attributes={{ 'test-selector': AppUtils.getTestSelector(`brand-${brand.name}`, node.type, node.settings?.testId) }}
            />
         ))}
      </CardGrid>
   );
};
